/**
 * Role icons (owner decisions of 2026-10-28), drawn at 16 px with the role's
 * name as each icon's tooltip and accessible name. The image is the server's
 * 64x64 copy, so it stays crisp at 125 % and 150 % display scaling.
 *
 * How many is the console's `display.role_icons` (live from the config):
 * - `highest` (the default, Discord): the icon of the person's highest role
 *   that has one (an uploaded image or an emoji);
 * - `all` (TeamSpeak): the icon of every role they hold that has one, top
 *   first; past `ROLE_ICONS_SHOWN_MAX` a "+N" chip names the rest.
 *
 * Where:
 * - Sidebar voice rows: immediately before the avatar (to its left), the
 *   highest nearest the avatar (Hub.tsx).
 * - Member list and message headers: right after the name, as Discord does.
 */
import { ROLE_ICONS_SHOWN_MAX, type Role, type Snowflake } from '@neosocial/protocol';
import { memo, type CSSProperties } from 'react';
import { useT } from '../i18n';
import { assetUrl } from '../lib/config';
import { useRoleIconMode } from '../store/config';
import { useDataShallow } from '../store/data';
import { iconRoles } from '../store/selectors';

/** Space between icons in a group (px). */
export const ROLE_ICON_GAP = 2;
/** The "+N" chip's width (px) for a one-digit N, and what each further digit adds. */
export const ROLE_ICON_MORE_W = 20;
export const ROLE_ICON_MORE_DIGIT_W = 6;

/**
 * The "+N" chip's width (px): set on the chip itself, so it is exactly what
 * a voice row reserves for it, and wide enough for N's digits ("+12" needs
 * about 21.5 px at the chip's 10 px bold, "+123" about 28).
 */
export function roleIconMoreWidth(n: number): number {
  const digits = String(Math.max(1, Math.floor(n))).length;
  return ROLE_ICON_MORE_W + ROLE_ICON_MORE_DIGIT_W * (digits - 1);
}

/**
 * The width of a voice row's icon group with `cols` icons and, when `more`
 * (the channel's biggest "+N") is above 0, the chip for it, so every row of
 * a channel reserves the widest group's space and the avatars stay in one
 * column.
 */
export function roleIconGroupWidth(cols: number, more: number, size = 16): number {
  if (cols <= 0) return 0;
  return (
    cols * size +
    (cols - 1) * ROLE_ICON_GAP +
    (more > 0 ? ROLE_ICON_GAP + roleIconMoreWidth(more) : 0)
  );
}

/** Draws one role's icon (also used by the role editor's preview). */
export const RoleIconView = memo(function RoleIconView({
  role,
  size = 16,
  cls,
  decorative,
}: {
  role: Pick<Role, 'name' | 'icon' | 'unicode_emoji'>;
  size?: number;
  cls?: string;
  /** No tooltip or label (the name is right next to it). */
  decorative?: boolean;
}) {
  const style = { '--ri': `${size}px` } as CSSProperties;
  const c = 'ns-ri' + (cls ? ' ' + cls : '');
  const label = decorative ? {} : { 'data-tip': role.name, 'aria-label': role.name };
  if (role.icon) {
    return (
      <img
        className={c}
        style={style}
        src={assetUrl(role.icon) ?? undefined}
        width={size}
        height={size}
        alt={decorative ? '' : role.name}
        draggable={false}
        decoding="async"
        {...label}
      />
    );
  }
  if (!role.unicode_emoji) return null;
  return (
    <span
      className={c + ' emoji'}
      style={style}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', ...label })}
    >
      {role.unicode_emoji}
    </span>
  );
});

/**
 * `all` mode: up to `ROLE_ICONS_SHOWN_MAX` icons, top first in the DOM (a
 * voice row lays them out from the avatar outwards), then a "+N" chip whose
 * tooltip and accessible name list the other roles. `width` fixes the group
 * (a voice row reserves its channel's widest group); with no roles it is
 * only that empty space.
 */
export function RoleIconGroup({
  roles,
  size = 16,
  cls,
  width,
}: {
  roles: readonly Role[];
  size?: number;
  cls?: string;
  width?: number;
}) {
  const { t, fmt } = useT();
  const shown = roles.slice(0, ROLE_ICONS_SHOWN_MAX);
  const more = roles.slice(ROLE_ICONS_SHOWN_MAX);
  const names = more.map((r) => r.name).join(', ');
  const style = width ? ({ width: `${width}px` } as CSSProperties) : undefined;
  if (!roles.length) {
    return (
      <span className={'ns-ris none' + (cls ? ' ' + cls : '')} style={style} aria-hidden="true" />
    );
  }
  return (
    <span className={'ns-ris' + (cls ? ' ' + cls : '')} style={style} data-n={roles.length}>
      {shown.map((r) => (
        <RoleIconView key={r.id} role={r} size={size} />
      ))}
      {more.length ? (
        <span
          className="ns-ri-more nx-num"
          style={{ width: `${roleIconMoreWidth(more.length)}px` }}
          role="img"
          data-tip={names}
          aria-label={t('roles.icons.more', { n: more.length, names })}
        >
          +{fmt.num(more.length)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A member's role icons, as the console says: their highest role's icon,
 * or every role's (`RoleIconGroup`). Nothing when no role of theirs has one.
 */
export function RoleIcon({
  guildId,
  userId,
  size = 16,
  cls,
}: {
  guildId: Snowflake;
  userId: Snowflake;
  size?: number;
  cls?: string;
}) {
  const mode = useRoleIconMode();
  const roles = useDataShallow((d) =>
    iconRoles(d.guilds[guildId], d.members[guildId]?.[userId], mode),
  );
  if (!roles.length) return null;
  if (mode !== 'all') return <RoleIconView role={roles[0]!} size={size} cls={cls} />;
  return <RoleIconGroup roles={roles} size={size} cls={cls} />;
}
